import React, { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToSection } from "@/hooks/useLenis";

export const Navbar = () => {
  const { t, lang, setLang, languages } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "gallery", label: t.nav.gallery },
    { id: "testimonials", label: t.nav.testimonials },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  const onLight = scrolled; // dark text when navbar has cream bg
  const txt = onLight ? "text-[#1A1A1A]" : "text-[#FAF9F6]";
  const txtSoft = onLight ? "text-[#1A1A1A]/80" : "text-[#FAF9F6]/85";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-[#1A1A1A]/8 py-4" : "bg-transparent py-6"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToSection("hero")}
          className={`font-display text-xl md:text-2xl tracking-[0.18em] uppercase ${txt} leading-none transition-colors duration-300`}
        >
          Olivier&nbsp;Boës
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`text-xs font-medium tracking-[0.18em] uppercase ${txtSoft} hover:text-[#C5A880] transition-colors duration-300`}
            >
              {l.label}
            </button>
          ))}

          <div className="relative">
            <button
              data-testid="lang-toggle"
              onClick={() => setLangOpen((v) => !v)}
              onBlur={() => setTimeout(() => setLangOpen(false), 150)}
              className={`flex items-center gap-1 text-xs font-medium tracking-[0.18em] uppercase ${txt} hover:text-[#C5A880] transition-colors`}
            >
              {lang.toUpperCase()}
              <ChevronDown size={14} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-3 w-36 bg-[#FAF9F6] border border-[#1A1A1A]/10 shadow-xl py-1">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onMouseDown={() => { setLang(l.code); setLangOpen(false); }}
                    className={`block w-full text-left px-4 py-2 text-xs tracking-[0.15em] uppercase transition-colors hover:bg-[#F3EFE7] ${
                      lang === l.code ? "text-[#B0926A]" : "text-[#1A1A1A]/80"
                    }`}
                  >
                    {l.label} · {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            data-testid="nav-book-btn"
            onClick={() => go("contact")}
            className={`px-6 py-3 text-xs font-medium tracking-[0.18em] uppercase transition-colors duration-300 ${
              onLight ? "bg-[#1A1A1A] text-[#FAF9F6] hover:bg-[#C5A880]" : "bg-[#FAF9F6] text-[#1A1A1A] hover:bg-[#C5A880] hover:text-[#FAF9F6]"
            }`}
          >
            {t.nav.book}
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden ${txt}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#FAF9F6] border-t border-[#1A1A1A]/8 mt-4">
          <div className="px-6 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left py-3 text-sm font-medium tracking-[0.18em] uppercase text-[#1A1A1A]/80"
              >
                {l.label}
              </button>
            ))}
            <div className="flex gap-2 py-4">
              {languages.map((l) => (
                <button
                  key={l.code}
                  data-testid={`mobile-lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-2 text-xs tracking-[0.15em] uppercase border ${
                    lang === l.code ? "border-[#B0926A] text-[#B0926A]" : "border-[#1A1A1A]/15 text-[#1A1A1A]/70"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <button
              data-testid="mobile-book-btn"
              onClick={() => go("contact")}
              className="mt-2 px-6 py-4 bg-[#1A1A1A] text-[#FAF9F6] text-xs font-medium tracking-[0.18em] uppercase"
            >
              {t.nav.book}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
